/**
 * B1067 — Historique des vérifications d'une synthèse
 * (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * B1065 affiche la date de la DERNIÈRE vérification. Ce panneau montre toutes
 * les précédentes : qui les a lancées, avec quel fournisseur, ce qu'elles ont
 * trouvé, et quelles corrections ont été appliquées ensuite. Sans cette
 * trace, un fait corrigé puis réintroduit par une édition ultérieure passait
 * totalement inaperçu.
 *
 * Chargement paresseux : la liste n'est demandée qu'à l'ouverture du panneau.
 *
 * B1123 (Eric 2026-09-08) — L'historique ne montrait que les vérifications et
 * les corrections. Les AJOUTS de révision manuelle et les MASQUAGES en étaient
 * absents : quand une précision disparaissait de la synthèse, plus rien ne
 * disait qui l'avait retirée. Ajout d'un filtre par utilisateur, parce que la
 * première question d'Eric devant une modification surprise est « qui ? ».
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { CaretDown, CaretRight, ClockCounterClockwise } from "@phosphor-icons/react";

const fmt = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
      dateStyle: "long",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
};

// B1123 — Un libellé par type d'événement : « vérification » pour tout
// afficherait un masquage humain comme un contrôle automatique.
const EVENT_STYLES = {
  correction: { cls: "bg-emerald-100 text-emerald-800", fr: "correction appliquée", en: "correction applied" },
  regression: { cls: "bg-amber-100 text-amber-900", fr: "erreur réapparue", en: "error reappeared" },
  revision_added: { cls: "bg-sky-100 text-sky-900", fr: "révision ajoutée", en: "revision added" },
  revision_hidden: { cls: "bg-zinc-200 text-zinc-800", fr: "révision masquée", en: "revision hidden" },
  revision_restored: { cls: "bg-zinc-200 text-zinc-800", fr: "révision réaffichée", en: "revision restored" },
  manual_review: { cls: "bg-zinc-200 text-zinc-800", fr: "revue manuelle", en: "manual review" },
  verification: { cls: "bg-[#002FA7]/10 text-[#002FA7]", fr: "vérification", en: "verification" },
};

export const SynthesisVerificationHistory = ({ api, videoId, lang = "fr" }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [author, setAuthor] = useState("");

  const load = useCallback(async (by = "") => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${api}/api/videos/${videoId}/synthesis/verification-history?limit=50${by ? `&by=${encodeURIComponent(by)}` : ""}`,
        { withCredentials: true }
      );
      // Les auteurs viennent du serveur et ne doivent PAS être écrasés par
      // une réponse filtrée, sinon le filtre se vide de ses options.
      setData((prev) => ({ ...res, authors: res.authors?.length ? res.authors : prev?.authors || [] }));
    } catch {
      setData({ events: [], error: true });
    } finally {
      setLoading(false);
    }
  }, [api, videoId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) load();
  };

  const events = data?.events || [];
  const authors = data?.authors || [];

  return (
    <section className="mt-6 not-prose" data-testid="synthesis-verification-history">
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-[#002FA7]"
        aria-expanded={open}
        data-testid="synthesis-history-toggle"
      >
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        <ClockCounterClockwise size={14} />
        {lang === "fr" ? "Historique des vérifications" : "Verification history"}
        {data?.count ? ` (${data.count})` : ""}
      </button>

      {open && (
        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
          {authors.length > 1 && (
            <div className="flex items-center gap-2 mb-3">
              <label className="text-[11px] text-zinc-500" htmlFor="synthesis-history-author">
                {lang === "fr" ? "Filtrer par utilisateur" : "Filter by user"}
              </label>
              <select
                id="synthesis-history-author"
                value={author}
                onChange={(ev) => { setAuthor(ev.target.value); load(ev.target.value); }}
                className="text-xs border border-zinc-300 rounded-md px-2 py-1 bg-white"
                data-testid="synthesis-history-author-filter"
              >
                <option value="">{lang === "fr" ? "Tous" : "All"}</option>
                {authors.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              {(data?.additions || data?.hidings) ? (
                <span className="text-[11px] text-zinc-500" data-testid="synthesis-history-counts">
                  {lang === "fr"
                    ? `${data.additions || 0} ajout(s) · ${data.hidings || 0} masquage(s)`
                    : `${data.additions || 0} addition(s) · ${data.hidings || 0} hiding(s)`}
                </span>
              ) : null}
            </div>
          )}
          {loading && (
            <p className="text-xs text-zinc-500" data-testid="synthesis-history-loading">
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </p>
          )}
          {!loading && events.length === 0 && (
            <p className="text-xs text-zinc-500" data-testid="synthesis-history-empty">
              {lang === "fr"
                ? "Aucune vérification enregistrée pour cette synthèse."
                : "No verification recorded for this synthesis."}
            </p>
          )}
          {!loading && events.length > 0 && (
            <ol className="space-y-3">
              {events.map((e) => (
                <li
                  key={e.id}
                  className="text-xs border-l-2 pl-3 border-zinc-300"
                  data-testid={`synthesis-history-event-${e.id}`}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        (EVENT_STYLES[e.event] || EVENT_STYLES.verification).cls
                      }`}
                    >
                      {(EVENT_STYLES[e.event] || EVENT_STYLES.verification)[lang === "fr" ? "fr" : "en"]}
                    </span>
                    <span className="text-zinc-700 font-medium">{fmt(e.at, lang)}</span>
                    <span className="text-zinc-500">· {e.by}</span>
                    {e.provider && <span className="text-zinc-400 font-mono">· {e.provider}</span>}
                    <span className={e.claims_count ? "text-red-700 font-medium" : "text-emerald-700"}>
                      ·{" "}
                      {String(e.event || "").startsWith("revision_") || e.event === "manual_review"
                        ? (lang === "fr" ? "1 révision" : "1 revision")
                        : e.event === "correction"
                        ? `${e.applied_count ?? e.claims_count} correction(s)`
                        : e.event === "regression"
                          ? (lang === "fr"
                              ? `${e.claims_count} erreur(s) réapparue(s)`
                              : `${e.claims_count} error(s) back`)
                          : e.claims_count
                          ? (lang === "fr"
                              ? `${e.claims_count} erreur(s) trouvée(s)`
                              : `${e.claims_count} error(s) found`)
                          : (lang === "fr" ? "aucune erreur" : "no error")}
                    </span>
                  </div>
                  {e.excerpt && (
                    <p className="mt-1 text-zinc-500 italic leading-snug" data-testid={`synthesis-history-excerpt-${e.id}`}>
                      « {e.excerpt} »
                    </p>
                  )}
                  {(e.issues || []).length > 0 && (
                    <ul className="mt-1.5 space-y-1 text-zinc-600">
                      {e.issues.map((i, idx) => (
                        <li key={i.id || idx} className="leading-snug">
                          <span className="text-zinc-400">•</span>{" "}
                          {i.issue || (lang === "fr" ? "sans description" : "no description")}
                          {i.original_text && (
                            <span className="block text-zinc-400 italic">« {i.original_text} »</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
};

export default SynthesisVerificationHistory;
